import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { getRouteMeta, siteConfig } from './metaConfig';

type ThemeMode = 'light' | 'dark';

const ensureMetaTag = (attributes: { name?: string; property?: string }) => {
  const selectorKey = attributes.name ? 'name' : 'property';
  const selectorValue =
    selectorKey === 'name' ? attributes.name : attributes.property;
  if (!selectorKey || !selectorValue) {
    throw new Error('Meta tags require either a name or property attribute.');
  }

  let tag = document.head.querySelector<HTMLMetaElement>(
    `meta[${selectorKey}="${selectorValue}"]`
  );

  if (!tag) {
    tag = document.createElement('meta');
    document.head.appendChild(tag);
  }

  tag.setAttribute(selectorKey, selectorValue);
  return tag;
};

const setMetaTag = (attributes: Record<string, string>) => {
  const tag = ensureMetaTag(attributes);
  Object.entries(attributes).forEach(([key, value]) => {
    tag.setAttribute(key, value);
  });
};

const setLinkTag = (rel: string, href: string) => {
  let link = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', rel);
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
};

const Seo = ({ theme }: { theme: ThemeMode }) => {
  const location = useLocation();

  useEffect(() => {
    const meta = getRouteMeta(location.pathname);
    const canonical =
      meta.path === '/' ? siteConfig.baseUrl : `${siteConfig.baseUrl}${meta.path}`;
    const imagePath = meta.image ?? siteConfig.defaultImage;
    const imageUrl = imagePath.startsWith('http')
      ? imagePath
      : `${siteConfig.baseUrl}${imagePath}`;
    const keywords =
      meta.keywords?.filter(Boolean).join(', ') ??
      `${siteConfig.siteName}, ${siteConfig.author}`;
    const themeColor =
      theme === 'dark' ? siteConfig.darkThemeColor : siteConfig.lightThemeColor;

    document.title = meta.title;

    setMetaTag({ name: 'description', content: meta.description });
    setMetaTag({ name: 'keywords', content: keywords });
    setMetaTag({ name: 'author', content: siteConfig.author });
    setMetaTag({ name: 'theme-color', content: themeColor });

    setLinkTag('canonical', canonical);

    setMetaTag({ property: 'og:type', content: meta.type ?? 'website' });
    setMetaTag({ property: 'og:site_name', content: siteConfig.siteName });
    setMetaTag({ property: 'og:title', content: meta.title });
    setMetaTag({ property: 'og:description', content: meta.description });
    setMetaTag({ property: 'og:url', content: canonical });
    setMetaTag({ property: 'og:image', content: imageUrl });
    setMetaTag({ property: 'og:image:alt', content: meta.title });

    setMetaTag({ name: 'twitter:card', content: 'summary_large_image' });
    setMetaTag({ name: 'twitter:title', content: meta.title });
    setMetaTag({ name: 'twitter:description', content: meta.description });
    setMetaTag({ name: 'twitter:image', content: imageUrl });
  }, [location.pathname, theme]);

  return null;
};

export default Seo;
